import type { BadgeTone } from './Badge';

export type StatusDomain = 'transaction' | 'ticket' | 'payout' | 'bonus' | 'redeem';

/**
 * The one status → colour map (spec §5.2). Labels stay with callers (i18n).
 * Transaction tones follow V1 getStatusPillFigma: success green, pending amber,
 * fail red, cancelled/expired gray — so Success vs Fail reads at a glance.
 */
const MAP: Record<StatusDomain, Record<string, BadgeTone>> = {
  transaction: {
    completed: 'success', complete: 'success', success: 'success', succeeded: 'success', paid: 'success', confirmed: 'success',
    // Money came back / settled positively — same green bucket as V1.
    refunded: 'success', failed_refunded: 'success',
    pending: 'warning', waiting: 'warning', created: 'warning', awaiting_payment: 'warning', partial: 'warning', partially_completed: 'warning',
    // In progress on our side or the operator's — amber like V1 pending-like.
    processing: 'warning', in_progress: 'warning', submitted: 'warning',
    retry_scheduled: 'warning', awaiting_admin: 'warning', approved: 'warning',
    retrying: 'warning',
    failed: 'danger', error: 'danger', declined: 'danger', rejected: 'danger',
    cancelled: 'neutral', canceled: 'neutral', expired: 'neutral', expired_refunded: 'neutral',
  },
  ticket: { pending: 'warning', received: 'warning', in_progress: 'info', resolved: 'success', closed: 'neutral' },
  payout: {
    pending: 'warning', approved: 'info', processing: 'info', completed: 'success', success: 'success',
    rejected: 'danger', failed: 'danger', cancelled: 'neutral', canceled: 'neutral',
  },
  bonus: { locked: 'neutral', active: 'primary', in_progress: 'primary', completed: 'success', claimed: 'success', expired: 'neutral' },
  redeem: { pending: 'warning', approved: 'success', declined: 'danger', rejected: 'danger', expired: 'neutral' },
};

export function statusTone(domain: StatusDomain, status: string | null | undefined): BadgeTone {
  const key = String(status ?? '').trim().toLowerCase();
  return MAP[domain][key] ?? 'neutral';
}
